import { useEffect, useCallback, useState } from 'react'
import { useRegistryStore, persistRegistryExpanded } from '../stores/registryStore'
import { useRecordFilters, useDebouncedValue } from '../hooks/useRecordFilters'
import { RecordTable } from './RecordTable'
import { RecordEditor } from './RecordEditor'
import { useFileBrowserStore } from '@/features/files/stores/fileBrowserStore'
import { api, getErrorMessage } from '@/shared/api/generated'
import type { RecordRow } from '@/shared/api/generated'
import { toast } from '@/shared/stores/toastStore'
import './registry.css'

interface RegistryBarProps {
  groupId: number
}

export function RegistryBar({ groupId }: RegistryBarProps) {
  const [registryEnabled, setRegistryEnabled] = useState(false)
  const {
    config, records, tags, tagFilters, globalFilter,
    expanded, sortColumn, sortDirection, views,
    editorOpen, editingRecord,
    hasMore, columnFilters,
    loadConfig, loadRecords, loadAllValues, loadViews, setTagFilter, setColumnFilter, setGlobalFilter,
    toggleExpanded, setSort, openEditor, closeEditor, reset,
    values,
  } = useRegistryStore()

  const navigateTo = useFileBrowserStore(s => s.navigateTo)

  useEffect(() => {
    if (!groupId) { setRegistryEnabled(false); return }
    let cancelled = false
    Promise.all([
      api.getGroup(groupId),
      loadConfig(),
    ]).then(([g]) => {
      if (cancelled) return
      setRegistryEnabled(g.registry_enabled)
      if (g.registry_enabled) {
        loadRecords(groupId)
        loadAllValues(groupId)
        loadViews(groupId)
      }
    }).catch(err => { if (!cancelled) { toast.error(getErrorMessage(err)); setRegistryEnabled(false) } })
    return () => { cancelled = true; reset() }
  }, [groupId])

  const handleToggle = useCallback(() => {
    const next = !expanded
    toggleExpanded()
    persistRegistryExpanded(groupId, next)
  }, [expanded, groupId, toggleExpanded])

  const debouncedTagFilters = useDebouncedValue(tagFilters, 150)
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 150)
  const debouncedGlobalFilter = useDebouncedValue(globalFilter, 150)
  const filtered = useRecordFilters(records, debouncedTagFilters, debouncedColumnFilters, debouncedGlobalFilter, sortColumn, sortDirection)

  const handleClickRecord = useCallback((rec: RecordRow) => {
    const s = useFileBrowserStore.getState()
    // Use group name as mount folder — works even from view mounts
    const groupFolder = s.currentGroupName || s.currentPath.replace(/^\//, '').split('/')[0] || ''
    navigateTo('/' + groupFolder + '/' + rec.path)
  }, [navigateTo])

  const handleEditRecord = useCallback((rec: RecordRow) => {
    openEditor(rec, groupId)
  }, [openEditor, groupId])

  const handleNewRecord = useCallback(() => {
    openEditor(null, groupId)
  }, [openEditor, groupId])

  const handleEditorSaved = useCallback(() => {
    loadRecords(groupId)
    useFileBrowserStore.getState().refresh()
  }, [loadRecords, groupId])

  const handleViewsChanged = useCallback(() => {
    loadViews(groupId)
  }, [loadViews, groupId])

  if (!config?.enabled || !registryEnabled) return null

  const entityPlural = config.entity_name_plural || 'Records'

  return (
    <div className="registry-bar" data-testid="registry-bar">
      <div className="registry-header" onClick={handleToggle}>
        <span className="registry-toggle">{expanded ? '▼' : '▶'}</span>
        <span className="registry-title" data-testid="registry-title">{entityPlural}</span>
        {expanded && (
          <>
            <input
              type="text"
              className="registry-global-filter"
              placeholder="Search…"
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
              onClick={e => e.stopPropagation()}
              data-testid="registry-global-filter"
            />
            <button
              className="registry-new-btn"
              onClick={e => { e.stopPropagation(); handleNewRecord() }}
              data-testid="registry-new-btn"
            >
              + New {config.entity_name || 'Record'}
            </button>
          </>
        )}
        <span className="registry-status" data-testid="registry-status">
          {hasMore
            ? `Loading ${records.length} ${entityPlural.toLowerCase()}…`
            : filtered.length === records.length
              ? `${records.length} ${entityPlural.toLowerCase()}`
              : `${filtered.length} of ${records.length} ${entityPlural.toLowerCase()}`}
        </span>
      </div>

      {expanded && (
        <div className="registry-body">
          <RecordTable
            records={filtered}
            tags={tags}
            labelFormat={config.label_format || '%05d'}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={setSort}
            onClickRecord={handleClickRecord}
            onEditRecord={handleEditRecord}
            views={views}
            groupId={groupId}
            onViewsChanged={handleViewsChanged}
            tagFilters={tagFilters}
            onTagFilter={setTagFilter}
            columnFilters={columnFilters}
            onColumnFilter={setColumnFilter}
            getAutocomplete={(tagId) => (values[tagId] || []).map(v => v.value).sort()}
          />
        </div>
      )}
      {editorOpen && (
        <RecordEditor
          record={editingRecord}
          groupId={groupId}
          onClose={closeEditor}
          onSaved={handleEditorSaved}
        />
      )}
    </div>
  )
}
